import React from "react";
import { TemplateProps, createTemplateUtils } from "../BaseTemplate";
import BasicInfoSection from "../../offer-wizard/BasicInfoSection";
import PublicPlanSummary from "../PublicPlanSummary";
import PublicFinancialSummary from "../PublicFinancialSummary";
import { analyticsService } from "../../../services/analyticsService";

const LuxuryTemplate: React.FC<TemplateProps> = ({
  offer,
  brandKit,
  termsAndConditions,
  editMode = false,
  onColorChange,
  showColorPicker,
  setShowColorPicker,
  onHeroUpload,
  onHeroRemove,
  uploadingHero = false,
  onTermsToggle,
  onTermsContentChange,
}) => {
  const utils = createTemplateUtils(brandKit);

  const handleClick = (e: React.MouseEvent) => {
    if (!editMode && analyticsService.isSessionActive()) {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      analyticsService.trackClick(
        (e.target as HTMLElement).tagName.toLowerCase(),
        x,
        y,
        "offer-page"
      );
    }
  };

  return (
    <div
      className="font-serif bg-gray-900 min-h-screen"
      onClick={handleClick}
      style={
        {
          "--primary-color": utils.colors.primary || "#1A1A1A",
          "--secondary-color": utils.colors.secondary || "#2D2D2D",
          "--accent-color": utils.colors.accent || "#FFD700",
        } as React.CSSProperties
      }
    >
      <div className="max-w-4xl mx-auto bg-gray-900 text-white">
        {/* Key Visual Section */}
        {(utils.keyVisualUrl || editMode) && (
          <div className="relative group mb-12">
            {utils.keyVisualUrl ? (
              <div className="relative">
                <img
                  src={utils.keyVisualUrl}
                  alt="Key Visual"
                  className="w-full h-80 object-cover"
                  style={{ filter: "brightness(0.8) contrast(1.1)" }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-transparent to-transparent"></div>
                {editMode && (
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-black group-hover:bg-opacity-60 transition-all duration-200 flex items-center justify-center">
                    <div className="hidden group-hover:flex space-x-4">
                      <button
                        onClick={() => {
                          const input = document.createElement("input");
                          input.type = "file";
                          input.accept = "image/jpeg,image/png,image/webp";
                          input.onchange = (e) => {
                            const file = (e.target as HTMLInputElement)
                              .files?.[0];
                            if (file) onHeroUpload?.(file);
                          };
                          input.click();
                        }}
                        className="px-4 py-2 bg-yellow-600 text-white font-medium hover:bg-yellow-700 transition-colors"
                        disabled={uploadingHero}
                      >
                        {uploadingHero ? "Uploading..." : "Podmień"}
                      </button>
                      <button
                        onClick={onHeroRemove}
                        className="px-4 py-2 bg-red-600 text-white font-medium hover:bg-red-700 transition-colors"
                      >
                        Usuń
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : editMode ? (
              <div className="h-48 border-2 border-dashed border-yellow-600 flex items-center justify-center">
                <button
                  onClick={() => {
                    const input = document.createElement("input");
                    input.type = "file";
                    input.accept = "image/jpeg,image/png,image/webp";
                    input.onchange = (e) => {
                      const file = (e.target as HTMLInputElement).files?.[0];
                      if (file) onHeroUpload?.(file);
                    };
                    input.click();
                  }}
                  className="px-6 py-3 bg-yellow-600 text-white font-medium hover:bg-yellow-700 transition-colors"
                  disabled={uploadingHero}
                >
                  {uploadingHero ? "Uploading..." : "Dodaj Key Visual"}
                </button>
              </div>
            ) : null}
          </div>
        )}

        {/* Luxury Header */}
        <div className="text-center mb-16 px-8 py-12">
          <div className="mb-8">
            <div className="flex justify-center mb-6">
              <div className="w-24 h-px bg-gradient-to-r from-transparent via-yellow-500 to-transparent"></div>
            </div>
            <h1
              className="text-5xl font-bold mb-6 tracking-wide"
              style={{ color: utils.colors.accent || "#FFD700" }}
            >
              {offer.title}
            </h1>
            <div className="flex justify-center mb-6">
              <div className="w-16 h-px bg-gradient-to-r from-transparent via-yellow-500 to-transparent"></div>
            </div>
            <p className="text-2xl text-gray-300 italic">
              Ekskluzywnie przygotowane dla {offer.clientName}
            </p>
          </div>

          <div
            className="bg-gray-800 border border-yellow-600 p-6 max-w-2xl mx-auto"
            style={{ borderColor: utils.colors.accent || "#FFD700" }}
          >
            <div className="grid grid-cols-3 gap-8 text-center">
              <div>
                <div className="text-sm text-gray-400 mb-1">Hotel</div>
                <div className="font-semibold text-yellow-400">
                  {offer.hotel.name}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-400 mb-1">Oferta</div>
                <div className="font-mono text-yellow-400">
                  #{offer.id.slice(-8).toUpperCase()}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-400 mb-1">Data</div>
                <div className="font-semibold text-yellow-400">
                  {utils.formatDate(new Date().toISOString())}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content Sections with luxury styling */}
        <div className="space-y-12 mb-16 px-8">
          <div className="bg-gray-800 border-l-4 border-yellow-600 p-8">
            <BasicInfoSection
              data={{
                title: offer.title,
                clientName: offer.clientName,
                clientEmail: offer.clientEmail,
                clientPhone: offer.clientPhone,
                description: offer.description || "",
                checkInDate: offer.checkInDate,
                checkOutDate: offer.checkOutDate,
                numberOfGuests: offer.numberOfGuests,
                numberOfRooms: offer.numberOfRooms,
                offerDays: offer.offerDays,
                currentDay: 1,
                netValue: offer.netValue,
              }}
              mode="public"
            />
          </div>
          <div className="bg-gray-800 border-l-4 border-yellow-600 p-8">
            <PublicPlanSummary offer={offer} />
          </div>
          <div className="bg-gray-800 border-l-4 border-yellow-600 p-8">
            {offer.financialSummary && (
              <PublicFinancialSummary
                financialSummary={offer.financialSummary}
              />
            )}
          </div>
        </div>

        {/* Terms and Conditions */}
        {termsAndConditions?.enabled ? (
          <div className="relative group bg-gray-800 border border-yellow-600 p-8 mb-16 mx-8">
            {editMode && (
              <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => onTermsToggle?.(false)}
                  className="px-3 py-1 bg-red-600 text-white text-sm hover:bg-red-700 transition-colors"
                >
                  Usuń
                </button>
              </div>
            )}
            <h3 className="text-2xl font-bold mb-6 text-yellow-400 text-center">
              ◆ Warunki Oferty ◆
            </h3>
            {editMode ? (
              <textarea
                value={termsAndConditions.content || ""}
                onChange={(e) => onTermsContentChange?.(e.target.value)}
                placeholder="Wprowadź warunki oferty..."
                className="w-full h-24 p-3 bg-gray-700 border border-yellow-600 text-white resize-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent font-serif"
              />
            ) : (
              <div
                className="text-gray-300 leading-relaxed"
                dangerouslySetInnerHTML={{
                  __html:
                    termsAndConditions.content?.replace(/\n/g, "<br>") || "",
                }}
              />
            )}
          </div>
        ) : editMode ? (
          <div className="border-2 border-dashed border-yellow-600 p-8 text-center mb-16 mx-8">
            <button
              onClick={() => onTermsToggle?.(true)}
              className="px-6 py-3 bg-yellow-600 text-white font-medium hover:bg-yellow-700 transition-colors"
            >
              Dodaj sekcję "Warunki oferty"
            </button>
          </div>
        ) : null}

        {/* Luxury Call to Action */}
        <div className="bg-gradient-to-b from-gray-800 to-gray-900 border-t border-yellow-600 p-12 text-center mb-16 mx-8">
          <h3 className="text-4xl font-bold text-yellow-400 mb-8">
            ◆ Prestiżowa Współpraca ◆
          </h3>
          <p className="text-gray-300 text-xl mb-12 leading-relaxed italic max-w-2xl mx-auto">
            Będzie nam wielkim zaszczytem zapewnić Państwu niezrównaną jakość
            obsługi i niezapomniane doświadczenia na najwyższym poziomie.
          </p>

          <div className="flex flex-col sm:flex-row justify-center gap-6 mb-12">
            <button
              className="px-12 py-4 text-black font-bold text-lg shadow-xl hover:shadow-2xl transition-all border-2"
              style={{
                backgroundColor: utils.colors.accent || "#FFD700",
                borderColor: utils.colors.accent || "#FFD700",
              }}
            >
              ✓ Akceptuję Prestiżową Ofertę
            </button>
            <button className="px-12 py-4 bg-transparent border-2 border-yellow-600 text-yellow-400 font-bold text-lg hover:bg-yellow-600 hover:text-black transition-colors">
              ◆ Życzę Modyfikacji
            </button>
          </div>

          {/* Contact Information */}
          <div className="bg-gray-800 border-2 border-yellow-600 p-8 max-w-lg mx-auto">
            <h4 className="font-bold text-yellow-400 text-2xl mb-6 text-center">
              ◆ {offer.hotel.name} ◆
            </h4>

            {offer.hotel.address && (
              <p className="text-gray-300 mb-6 text-center italic">
                {offer.hotel.address}
              </p>
            )}

            <div className="space-y-4 text-center">
              {offer.hotel.phone && (
                <div>
                  <a
                    href={`tel:${offer.hotel.phone}`}
                    className="text-yellow-400 hover:text-yellow-300 font-medium text-lg"
                  >
                    ☎ {offer.hotel.phone}
                  </a>
                </div>
              )}
              {offer.hotel.email && (
                <div>
                  <a
                    href={`mailto:${offer.hotel.email}`}
                    className="text-yellow-400 hover:text-yellow-300 font-medium text-lg"
                  >
                    ✉ {offer.hotel.email}
                  </a>
                </div>
              )}
              {offer.hotel.website && (
                <div className="pt-4 border-t border-yellow-600">
                  <a
                    href={offer.hotel.website}
                    className="text-yellow-400 hover:text-yellow-300 font-medium"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    ◆ {offer.hotel.website} ◆
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Luxury Footer */}
        <footer className="text-center border-t-2 border-yellow-600 py-8 px-8">
          <div className="text-yellow-400 font-medium">
            <p className="mb-4 text-lg">
              ◆ Oferta zachowuje ekskluzywność do{" "}
              {utils.formatDate(
                new Date(
                  new Date(offer.publishedAt || offer.createdAt).getTime() +
                    30 * 24 * 60 * 60 * 1000
                ).toISOString()
              )}{" "}
              ◆
            </p>
            <p className="text-gray-400 italic">
              Z szacunkiem i najwyższym uznaniem
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default LuxuryTemplate;
